import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Text } from '@quintoandar-tokko/jopijs';
import BaseModal from '../../BaseModal';
import {
  clearPerformanceModalFilters,
  setPerformanceModals,
} from '@/store/slices/components/dashboard/DashboardSlice';
import buildQuery from '@/components/Dashboard/utils/buildQuery';
import { getPerformanceConversionTimeTable } from '@/store/slices/components/dashboard/service';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import DownloadButton from '@/components/Dashboard/DashboardTables/components/DownloadButton';
import styles from '../../performanceModals.module.scss';
import buildTableQuery from '@/components/Dashboard/utils/buildTableQuery';
import useAsyncTableExcelDownload from '@/components/Dashboard/customHooks/useAsyncTableExcelDownload';
import { TABLES } from '@/components/Dashboard/constants';
import TableSearchEmptyState from '@/components/Dashboard/DashboardTables/TableSearchEmptyState';
import SearchInput from '../../SearchInput';
import {
  conversionTimeStatusOptions,
  conversionTimeTypesOptions,
} from '../utils';
import ConversionTimeTable from '@/components/Dashboard/DashboardTables/ConversionTimeTable';
import TableDropdown from '@/components/Dashboard/DashboardTables/components/TableDropdown';
import Checkbox from '@/components/checkbox/Checkbox';
import UseShowGroupByBranch from '@/components/Dashboard/utils/UseShowGroupByBranch';
import { AGENT } from '@/components/FilterBar/constants/filtersConstants';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function ConversionTimeModal() {
  const dispatch = useDispatch();
  const { selectedIds, selectBranches, selectAgents } = useSelector(
    (state) => state.FilterBarSlice.filters
  );
  const { conversionTime } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const {
    median_conversion_time,
    list,
    loading,
    sortBy,
    sortDirection,
    searchQuery,
    searchTypeFlag,
    searchStatus,
    groupByBranch,
  } = conversionTime;
  const inputMaxWidth =
    selectBranches.length > 1 || selectAgents.length > 1 ? 552 : '100%';

  const showGroupByBranch = UseShowGroupByBranch();
  const track = useTrackPerformanceTable();
  const { handleDownloadExcel: downloadExcel, isDownloading } =
    useAsyncTableExcelDownload(TABLES.CONVERSION_TIME);

  useEffect(() => {
    const fetchData = () => {
      const query = buildQuery(selectedIds);
      const tableQuery = buildTableQuery(conversionTime);

      dispatch(getPerformanceConversionTimeTable({ query, tableQuery }));
    };

    fetchData();
  }, [
    sortBy,
    sortDirection,
    searchQuery,
    searchTypeFlag,
    searchStatus,
    groupByBranch,
  ]);

  const handleDownloadExcel = () => {
    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(conversionTime);
    downloadExcel(query, tableQuery);
    track.conversionTime(TRACK_ELEMENT.DOWNLOAD_BUTTON);
  };

  const handleClearFilters = () => {
    const modal = 'conversionTime';
    const filters = [
      'searchQuery',
      'searchType',
      'searchStatus',
      'groupByBranch',
    ];
    dispatch(clearPerformanceModalFilters({ modal, filters }));
  };

  const selectedStatusOption =
    conversionTimeStatusOptions.find(
      (option) => option.value === searchStatus
    ) ?? conversionTimeStatusOptions[1];

  const uniqueAgent =
    selectedIds.filter((x) => x.filter_type === AGENT).length === 1;

  const handleBranchGroupCheck = () => {
    dispatch(
      setPerformanceModals({
        key: 'conversionTime',
        subkey: 'groupByBranch',
        value: !groupByBranch,
      })
    );
    track.conversionTime(TRACK_ELEMENT.BRANCH_GROUP_CHECKBOX);
  };

  const handleAgentBranchChange = (option) => {
    dispatch(
      setPerformanceModals({
        key: 'conversionTime',
        subkey: 'searchStatus',
        value: option.value,
      })
    );
    track.conversionTime(TRACK_ELEMENT.DROPDOWN_SHOW_ALL);
  };

  return (
    <BaseModal
      id="conversion-time-modal"
      title={`${Trans(translations, 'MedianConversionTime')}: ${
        median_conversion_time ?? '0'
      }`}
      handleClose={() =>
        dispatch(
          setPerformanceModals({
            key: 'conversionTime',
            subkey: 'isOpen',
            value: false,
          })
        )
      }
    >
      <Box className={styles.modalControls}>
        <SearchInput
          modal="conversionTime"
          dropdownOptions={conversionTimeTypesOptions}
          maxWidth={inputMaxWidth}
        />

        {showGroupByBranch && (
          <Box
            className={styles.checkboxWrapper}
            onClick={handleBranchGroupCheck}
          >
            <Checkbox checked={groupByBranch} />
            <Box as="span" className={styles.checkboxText}>
              {Trans(translations, 'GroupByBranch')}
            </Box>
          </Box>
        )}

        {(selectAgents.length > 1 || selectBranches.length > 1) &&
          !uniqueAgent && (
            <Box className={styles.dropdownWrapper}>
              <Text className={styles.dropdownWrapperLabel}>
                {Trans(translations, 'NewContactsTable.Show')}
              </Text>
              <TableDropdown
                selectedOption={selectedStatusOption}
                options={conversionTimeStatusOptions}
                onSelect={(option) => handleAgentBranchChange(option)}
              />
            </Box>
          )}

        <DownloadButton
          onClick={handleDownloadExcel}
          disabled={isDownloading}
        />
      </Box>
      {searchQuery && list?.length === 0 && !loading ? (
        <TableSearchEmptyState onClearFilters={handleClearFilters} />
      ) : (
        <BaseModal.TableContainer>
          <ConversionTimeTable />
        </BaseModal.TableContainer>
      )}
    </BaseModal>
  );
}
